/**
 * Settings → Audit → Security contacts (`/admin/v1/security-contacts`): the people and directory groups told at once,
 * in their Inbox and on their phones, when an agent uses a decoy key, a change is held back because it carried a
 * secret, or a locked secret is changed through emergency access. With nobody named, the administrators are told.
 */

import type { SecurityContact } from '@aioe/shared';
import { Persons, Shield } from '@gravity-ui/icons';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { groupsAvailable } from '../directory.ts';
import { useLoad } from '../hooks.ts';
import { useStepUp } from './rolesPermissions.tsx';
import { SubjectName, SubjectPicker } from './SubjectPicker.tsx';
import { ErrorNote, ListGroup, ListRow } from './ui.tsx';
import { RowMenu } from './ui/RowMenu.tsx';

export function SecurityContactsPanel() {
  const list = useLoad(() => routes.securityContacts(), []);
  const step = useStepUp();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const contacts = list.data?.contacts ?? [];
  const save = async (next: SecurityContact[]) => {
    setError(undefined);
    setBusy(true);
    try {
      await step(() => routes.setSecurityContacts(next), 'Sign in again to change who hears of security alerts.');
      list.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (list.error) return <ErrorNote error={list.error} />;
  if (!list.data) return null;
  const groups = groupsAvailable();
  return (
    <ListGroup
      id="security-contacts"
      title="Security contacts"
      count={contacts.length || undefined}
      description="Told at once, in their Inbox and on their phones, when an agent uses a decoy key, a change is held back because it carried a secret, or a locked secret is changed through emergency access. The audit trail, forwarding and Microsoft Sentinel carry the same events."
    >
      {contacts.length === 0 ? <ListRow icon={<Shield />} title="The administrators" description="Nobody is named, so the organisation’s administrators are told." /> : null}
      {contacts.map((c) => (
        <ListRow
          key={`${c.subjectType}/${c.subjectId}`}
          icon={<Persons />}
          title={<SubjectName id={c.subjectId} fallback={c.subjectType === 'group' ? 'A directory group' : undefined} />}
          description={c.subjectType === 'group' ? 'Group: everyone in it' : 'Person'}
          control={<RowMenu label="More for this security contact" items={[{ id: 'remove', label: 'Remove as a security contact', danger: true }]} onAction={() => void save(contacts.filter((x) => !(x.subjectType === c.subjectType && x.subjectId === c.subjectId)))} />}
        />
      ))}
      <ListRow title="Add a security contact" description={groups ? 'Find a person or a directory group.' : 'Find a person.'}>
        <SubjectPicker
          className="w-full"
          kinds={['user', 'group']}
          placeholder={groups ? 'A person or group by name' : 'A person by name'}
          onPick={(s) => !busy && void save([...contacts.filter((x) => !(x.subjectType === s.type && x.subjectId === s.id)), { subjectType: s.type, subjectId: s.id }])}
        />
      </ListRow>
      {error ? <ErrorNote error={error} /> : null}
    </ListGroup>
  );
}
